// src/components/cards/TodaysOverdueCard.tsx
import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { useNavigate } from 'react-router-dom';
import { invoiceAPI, OverdueInvoiceItem } from '../../services/api';
import LoadingSpinner from '../common/LoadingSpinner';

interface TodaysOverdueCardProps {
  count?: number;
  date?: string;
}

const CardContainer = styled(Box)(({ theme }) => ({
  backgroundColor:
    theme.palette.mode === 'dark' ? theme.palette.grey[900] : '#ffffff',
}));

const HeaderRow = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'flex-start',
  alignItems: 'center',
  padding: '1.5rem 2rem',
  borderBottom: `1px solid ${theme.palette.divider}`,
  gap: '2rem',
  flexWrap: 'wrap',
}));

const CountText = styled(Typography)(({ theme }) => ({
  fontSize: '1.8rem',
  fontWeight: 500,
  color: theme.palette.error.main,
  whiteSpace: 'nowrap',
}));

const EmptyState = styled(Box)(({ theme }) => ({
  textAlign: 'center',
  padding: '3rem 2rem',
  color: theme.palette.text.secondary,
}));

const ClickableRow = styled(TableRow)(({ theme }) => ({
  cursor: 'pointer',
  '&:hover': {
    backgroundColor:
      theme.palette.mode === 'dark'
        ? theme.palette.grey[800]
        : theme.palette.grey[50],
  },
}));

const getDaysOverdueColor = (days: number): 'warning' | 'error' | 'default' => {
  if (days <= 7) return 'warning';
  return 'error';
};

const TodaysOverdueCard: React.FC<TodaysOverdueCardProps> = ({
  count,
  date,
}) => {
  const navigate = useNavigate();
  const [invoices, setInvoices] = useState<OverdueInvoiceItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setLoading(true);
    setError(null);
    invoiceAPI
      .getOverdue(date)
      .then(setInvoices)
      .catch(() => setError('Failed to load overdue invoices'))
      .finally(() => setLoading(false));
  }, [date]);

  const displayCount = count ?? invoices.length;

  if (loading) {
    return (
      <CardContainer>
        <LoadingSpinner message="Loading..." size={40} />
      </CardContainer>
    );
  }

  if (error) {
    return (
      <CardContainer>
        <EmptyState>
          <Typography variant="body1" color="error">
            {error}
          </Typography>
        </EmptyState>
      </CardContainer>
    );
  }

  return (
    <CardContainer>
      <HeaderRow>
        <CountText>
          {displayCount} Overdue Invoice{displayCount !== 1 ? 's' : ''}
        </CountText>
      </HeaderRow>

      {invoices.length === 0 ? (
        <EmptyState>
          <Typography variant="body1">No overdue invoices</Typography>
          <Typography variant="body2" sx={{ mt: 0.5 }}>
            All invoices are up to date.
          </Typography>
        </EmptyState>
      ) : (
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Invoice</TableCell>
                <TableCell>Client</TableCell>
                <TableCell>Booking</TableCell>
                <TableCell>Due Date</TableCell>
                <TableCell align="right">Outstanding</TableCell>
                <TableCell align="center">Days Overdue</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {invoices.map((inv) => (
                <ClickableRow
                  key={inv.id}
                  onClick={() => navigate(`/clients/${inv.clientId}/account`)}
                >
                  <TableCell>
                    <Typography variant="body2" fontWeight={600}>
                      {inv.invoiceNumber}
                    </Typography>
                  </TableCell>
                  <TableCell>{inv.clientName}</TableCell>
                  <TableCell>
                    <Typography
                      variant="body2"
                      sx={{ color: 'primary.main', cursor: 'pointer' }}
                      onClick={(e) => {
                        e.stopPropagation();
                        navigate(`/booking/${inv.bookingId}`);
                      }}
                    >
                      #{inv.bookingId}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    {new Date(inv.dueDate).toLocaleDateString('en-ZA')}
                  </TableCell>
                  <TableCell align="right">
                    <Typography variant="body2" color="error" fontWeight={600}>
                      R {inv.outstandingAmount.toFixed(2)}
                    </Typography>
                  </TableCell>
                  <TableCell align="center">
                    <Chip
                      label={`${inv.daysOverdue}d`}
                      color={getDaysOverdueColor(inv.daysOverdue)}
                      size="small"
                    />
                  </TableCell>
                </ClickableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </CardContainer>
  );
};

export default TodaysOverdueCard;
